import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { useToast } from '../../hooks/use-toast';
import useSEO from '../../components/SEO';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    useSEO({
        title: "Réinitialisation du mot de passe - ISTMECH",
        description: "Définissez votre nouveau mot de passe.",
        url: "https://www.istmech.edu.ht/reset-password",
        robots: "noindex, nofollow" // Don't index this page
    });

    useEffect(() => {
        if (!token) {
            toast({
                variant: "destructive",
                title: "Lien invalide",
                description: "Le lien de réinitialisation est manquant ou invalide.",
            });
            navigate('/login');
        }
    }, [token, navigate, toast]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast({
                variant: "destructive",
                title: "Erreur",
                description: "Les mots de passe ne correspondent pas.",
            });
            return;
        }

        if (newPassword.length < 8) {
            toast({
                variant: "destructive",
                title: "Mot de passe trop court",
                description: "Le mot de passe doit contenir au moins 8 caractères.",
            });
            return;
        }

        setIsLoading(true);

        try {
            await api.post('/auth/reset-password', {
                token,
                newPassword
            });

            setIsSuccess(true);
            toast({
                title: "Succès",
                description: "Votre mot de passe a été réinitialisé avec succès.",
            });

            // Redirect after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error: any) {
            console.error(error);
            const errorMessage = error.response?.data?.message || "Une erreur est survenue. Le lien a peut-être expiré.";
            toast({
                variant: "destructive",
                title: "Erreur",
                description: errorMessage,
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
                <Card className="w-full max-w-md border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800">
                    <CardContent className="pt-6 text-center space-y-4">
                        <CheckCircle2 className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto" />
                        <h2 className="text-2xl font-bold text-green-900 dark:text-green-100">Mot de passe modifié !</h2>
                        <p className="text-green-700 dark:text-green-300">
                            Vous allez être redirigé vers la page de connexion dans quelques instants...
                        </p>
                        <Button className="w-full mt-4 bg-green-600 hover:bg-green-700" onClick={() => navigate('/login')}>
                            Se connecter maintenant
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>Nouveau mot de passe</CardTitle>
                    <CardDescription>
                        Choisissez un mot de passe fort pour sécuriser votre compte.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                            <Input
                                id="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                disabled={isLoading}
                                minLength={8}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={isLoading}
                                minLength={8}
                            />
                        </div>
                        <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Modification en cours...
                                </>
                            ) : (
                                'Changer le mot de passe'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResetPassword;
